import React, { ReactNode, useCallback, useEffect, useMemo, useReducer } from 'react';
import classNames from 'classnames';

import { CircularProgress, Container, makeStyles, Paper, Typography } from '@material-ui/core';

import { useUserContext } from '@hydra/common/context';
import { Tabs } from '@hydra/common/components';

import { useI18n } from '@hydra/common/customHooks';
import { hasSubUserAccess, isMasterUser } from '@hydra/utils/userUtil';

interface State {
  loading: boolean,
  selectedTabId: string
}

const reducer = (previousState: State, action: any) => {
  switch (action.type) {
    case 'SET_TAB':
      return {
        ...previousState,
        loading: false,
        selectedTabId: action.selectedTabId
      };
      break;
    default:
      throw new Error(`Unsupported action type: ${action.type}`);
  }
};

const useStyles = makeStyles(theme => ({
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  },
  paper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2.5
  },
  mt4: {
    marginTop: theme.spacing(4)
  }
}));

function AccountTabs ({ children }: { children: ReactNode }) {
  const classes = useStyles();
  const [state, dispatch] = useReducer(reducer, {
    loading: true,
    selectedTabId: 'security'
  });
  const { locale, translate } = useI18n();
  const { user } = useUserContext();

  const tabs = useMemo(() => {
    const tabs: Array<{
      id: string,
      label: string,
      dataUrl: string,
      hashUrl?: boolean
    }> = [{ id: 'security', label: translate('account_security_title'), dataUrl: `/${locale}/account/security`, hashUrl: false }];

    if (isMasterUser(user)) {
      tabs.push({ id: 'subscription', label: translate('account_subscription_title'), dataUrl: `/${locale}/account/subscription`, hashUrl: false });
      tabs.push({ id: 'billing', label: translate('account_billing_title'), dataUrl: `/${locale}/account/billing`, hashUrl: false });
      tabs.push({ id: 'api-keys', label: translate('account_api_title'), dataUrl: `/${locale}/account/api-keys`, hashUrl: false });
    }

    if (hasSubUserAccess(user)) {
      tabs.push({ id: 'subusers', label: translate('account_subusers_users_management'), dataUrl: `/${locale}/account/subusers`, hashUrl: false });
    }
    return tabs;
  }, [locale, translate, user]);

  const setTab = useCallback((id: string) => {
    dispatch({ type: 'SET_TAB', selectedTabId: id });
  }, []);

  useEffect(() => {
    const route = /[^/]*?(?=\?|\/?$)/i.exec(window.location.href)?.[0]?.toLowerCase();

    switch (route) {
      case 'billing':
      case 'security':
      case 'subscription':
      case 'api-keys':
      case 'subusers':
        setTab(route);
        break;
      default:
        setTab('security');
        break;
    }
  }, [setTab]);

  if (state.loading) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  let selectedTabIndex = tabs.findIndex((tab: any) => tab.id === state.selectedTabId);

  if (!selectedTabIndex || selectedTabIndex < 0) {
    selectedTabIndex = 0;
  }

  return (
    <>
      {tabs?.length > 1 && (
        <Tabs
          value={selectedTabIndex}
          ariaLabel={translate('account_title')}
          tabs={tabs}
        />
      )}
      <Container
        maxWidth='lg'
        className={classNames(
          'nr-account-page',
          tabs?.length <= 1 ? classes.mt4 : ''
        )}
      >
        <Paper elevation={0} className={classes.paper}>
          <Typography component='div' role='tabpanel'>
            {children}
          </Typography>
        </Paper>
      </Container>
    </>
  );
}

export default AccountTabs;
